import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Mail, Trash2, Shield, CheckCircle2 } from "lucide-react";

export default function DeleteAccount() {
  return (
    <PublicShell>
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="delete-account-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-rose-500/10 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-6 h-6 text-rose-500" />
          </div>
          <div>
            <h1 className="font-display text-4xl font-light tracking-tight">Delete your account</h1>
            <p className="text-sm text-muted-foreground mt-1">Erase your Treasurescare account and personal data.</p>
          </div>
        </div>

        {/* Intro */}
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm mb-8">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <b>Account deletion is permanent.</b> Once processed, your account, devices, active claims and gems balance cannot be restored.
              Some data (invoices, claim records) must be retained for legal reasons — see "What is retained" below.
            </div>
          </div>
        </div>

        {/* Method 1 — In-app */}
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-3">Option 1 — Delete from inside the app</h2>
          <ol className="list-decimal pl-6 space-y-1.5 text-sm text-foreground/90 leading-relaxed">
            <li>Sign in to the Treasurescare app or website.</li>
            <li>Go to <b>Settings</b> → <b>Data &amp; Privacy</b>.</li>
            <li>Click <b>Delete my account</b>.</li>
            <li>Confirm your password.</li>
            <li>Your request enters our queue and is processed within 30 days.</li>
          </ol>
        </section>

        {/* Method 2 — Email */}
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-3">Option 2 — Request by email</h2>
          <p className="text-sm text-foreground/90 leading-relaxed mb-3">
            Email us from the address registered to your account. We will verify your identity and process the deletion within 30 days.
          </p>
          <a
            href="mailto:support@treasurescare.com?subject=Account%20deletion%20request&body=Please%20delete%20my%20Treasurescare%20account%20and%20personal%20data.%0A%0AAccount%20email%3A%20%5Byour%20email%5D%0AConfirmation%3A%20Yes%2C%20I%20understand%20this%20is%20permanent."
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            data-testid="delete-email-btn"
          >
            <Mail className="w-4 h-4" />
            Email support@treasurescare.com
          </a>
        </section>

        {/* What gets deleted */}
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-3">What is deleted</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/30">
              <div className="flex items-center gap-2 text-emerald-500 text-sm font-medium mb-2">
                <CheckCircle2 className="w-4 h-4" /> Fully deleted within 30 days
              </div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-foreground/80">
                <li>Profile: name, email, phone, address, password hash</li>
                <li>Registered devices (make, model, IMEI, photos)</li>
                <li>Uploaded photos and documents</li>
                <li>Marketing preferences and list memberships</li>
                <li>Session tokens and login history</li>
                <li>Gems balance and daily-spin history</li>
                <li>Referral code and referral relationships</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-muted border border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium mb-2">
                <Shield className="w-4 h-4" /> Retained (legal obligation)
              </div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-foreground/80">
                <li>Completed claim records — 6 years (Consumer Rights Act)</li>
                <li>Invoices and payment records — 6 years (HMRC)</li>
                <li>Audit-log entries for admin actions — 12 months</li>
                <li>Fraud/security alerts — 24 months</li>
              </ul>
              <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                Retained records are anonymised where possible — your name is replaced with an internal ID.
              </p>
            </div>
          </div>
        </section>

        {/* Data export first */}
        <section className="mb-10 p-5 rounded-lg border border-primary/20 bg-primary/5">
          <h3 className="font-display text-lg mb-1">Want a copy of your data first?</h3>
          <p className="text-sm text-foreground/80 mb-3">
            Before deleting, you can request a machine-readable export of your data under UK GDPR Article 20 (right to data portability).
          </p>
          <a href="mailto:support@treasurescare.com?subject=Data%20export%20request" className="text-primary hover:underline text-sm">
            Email us for a data export →
          </a>
        </section>

        {/* Timeline */}
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-3">Timeline</h2>
          <ol className="list-decimal pl-6 space-y-2 text-sm text-foreground/90 leading-relaxed">
            <li><b>Day 0</b> — you submit your deletion request.</li>
            <li><b>Day 1–3</b> — we verify identity by replying to your registered email.</li>
            <li><b>Day 3–30</b> — the deletion is processed. Active subscriptions are cancelled pro-rata.</li>
            <li><b>Day 30</b> — confirmation email that deletion is complete.</li>
          </ol>
        </section>

        {/* CTA / back */}
        <section className="pt-6 border-t border-border text-center text-xs text-muted-foreground">
          Changed your mind? <Link to="/" className="text-primary hover:underline">Return to Treasurescare</Link> · <Link to="/privacy" className="hover:text-foreground">Privacy policy</Link> · <Link to="/terms" className="hover:text-foreground">Terms &amp; conditions</Link>
        </section>
      </article>
    </PublicShell>
  );
}
